import React, { useState } from "react";
import { useFetch } from "@/hooks/useApi";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader, Loader, StatusPill, Plate, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { gbp, num, ukDate, todayISO } from "@/lib/format";
import * as Icons from "lucide-react";
import { toast } from "sonner";

export default function DriverFuel() {
  const { data: me } = useFetch("/drivers/me");
  const { data: logs, loading, refetch } = useFetch("/fuel");
  const [form, setForm] = useState({ date: todayISO(), van_reg: "", miles_driven: "", litres: "", price_per_litre: "", station: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/fuel", { ...form, van_reg: form.van_reg || me?.assigned_van_reg });
      toast.success("Fuel logged");
      setForm({ date: todayISO(), van_reg: "", miles_driven: "", litres: "", price_per_litre: "", station: "" });
      refetch();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  if (loading) return <Loader />;

  return (
    <div className="fp-fade-in max-w-2xl mx-auto">
      <PageHeader title="Fuel Log" subtitle="Record your fuel purchases" testId="driver-fuel-page" />
      <Card className="p-6 mb-6">
        <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4" data-testid="driver-fuel-form">
          <div><Label>Date</Label><Input type="date" value={form.date} onChange={set("date")} className="mt-1.5" /></div>
          <div><Label>Van</Label><Input value={form.van_reg || me?.assigned_van_reg || ""} onChange={set("van_reg")} className="mt-1.5 font-mono" placeholder={me?.assigned_van_reg} /></div>
          <div><Label>Miles Driven</Label><Input type="number" value={form.miles_driven} onChange={set("miles_driven")} className="mt-1.5" data-testid="driver-fuel-miles" /></div>
          <div><Label>Litres</Label><Input type="number" step="0.01" value={form.litres} onChange={set("litres")} className="mt-1.5" data-testid="driver-fuel-litres" /></div>
          <div><Label>Price/Litre (£)</Label><Input type="number" step="0.001" value={form.price_per_litre} onChange={set("price_per_litre")} className="mt-1.5" /></div>
          <div><Label>Station</Label><Input value={form.station} onChange={set("station")} className="mt-1.5" /></div>
          <Button type="submit" className="sm:col-span-2 h-12" data-testid="driver-fuel-submit"><Icons.Fuel className="h-4 w-4 mr-2" /> Log Fuel</Button>
        </form>
      </Card>

      <h3 className="font-display font-semibold mb-3">Recent Fuel</h3>
      <div className="space-y-3">
        {(logs || []).length === 0 && <EmptyState icon="Fuel" title="No fuel logs yet" />}
        {(logs || []).map((l) => (
          <Card key={l.id} className="p-4 flex items-center justify-between" data-testid={`my-fuel-${l.id}`}>
            <div>
              <div className="flex items-center gap-2"><Plate reg={l.van_reg} /><span className="text-sm text-muted-foreground">{ukDate(l.date)}</span></div>
              <p className="text-sm mt-1 font-mono">{num(l.miles_driven)} mi · {l.litres}L · {l.miles_per_litre} mi/L</p>
            </div>
            <div className="text-right">
              <p className="font-mono font-semibold">{gbp(l.total_cost)}</p>
              <StatusPill status={l.variance_status} />
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
